import { useState, useEffect, useCallback } from 'react';
import { getSupabase } from '@/lib/supabaseClient.js';
import { useToast } from "@/components/ui/use-toast";

function useAuthSession() {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [initialAuthCheckComplete, setInitialAuthCheckComplete] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isEmailConfirmed, setIsEmailConfirmed] = useState(false);
  const [lastAuthEvent, setLastAuthEvent] = useState('INITIAL_SESSION');
  const { toast } = useToast();

  const refreshSessionData = useCallback(async (currentSession) => {
    if (currentSession?.user) {
      // Keep user object stable when only the token refreshes to avoid global refetch storms
      setSession(currentSession);
      setIsEmailConfirmed(!!currentSession.user.email_confirmed_at);
      setUser(prev => {
        if (prev?.id === currentSession.user.id) return prev || currentSession.user;
        return currentSession.user;
      });

      const supabase = getSupabase();
      if (supabase) {
        try {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', currentSession.user.id)
            .single();

          if (profileError && profileError.code !== 'PGRST116') {
            if (process.env.NODE_ENV === 'development') {
              console.error("Error fetching user role:", profileError);
            }
            setUserRole('user');
          } else {
            setUserRole(profileData?.role || 'user');
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error("Error fetching user role:", error);
          }
          setUserRole('user');
          toast({ title: "Role Error", description: "Could not determine user role.", variant: "destructive" });
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.warn("[useAuthSession] Supabase client not available for role fetch.");
        }
        setUserRole('user');
      }
    } else {
      // Clear all auth state when there's no session
      setUser(null);
      setSession(null);
      setUserRole(null);
      setIsEmailConfirmed(false);
    }
  }, [toast]);

  useEffect(() => {
    const supabase = getSupabase();
    if (!supabase) {
      if (process.env.NODE_ENV === 'development') {
        console.error("[useAuthSession] Supabase client is not initialized. Auth operations will fail.");
      }
      setLoadingSession(false);
      setInitialAuthCheckComplete(true);
      return;
    }
    
    setLoadingSession(true);
    
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session: currentSession } }) => {
      await refreshSessionData(currentSession);
      setLoadingSession(false);
      setInitialAuthCheckComplete(true);
    }).catch(error => {
      if (process.env.NODE_ENV === 'development') {
        console.error("[useAuthSession] Error in initial getSession:", error);
      }
      setLoadingSession(false);
      setInitialAuthCheckComplete(true);
    });

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      setLastAuthEvent(event);
      if (event === 'TOKEN_REFRESHED') {
        // Avoid resetting user/profile state on token refresh to prevent re-mount/re-fetch loops
        setSession(newSession);
        setIsEmailConfirmed(!!newSession?.user?.email_confirmed_at);
        setLoadingSession(false);
        return;
      }
      await refreshSessionData(newSession);
      if (event === 'INITIAL_SESSION') {
        setInitialAuthCheckComplete(true);
      }
      setLoadingSession(false);
    });

    return () => {
      if (authListener && typeof authListener.subscription?.unsubscribe === 'function') {
        authListener.subscription.unsubscribe();
      }
    };
  }, [refreshSessionData]);

  const refreshAuthData = async () => {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        await refreshSessionData(null);
        return;
      }
      const { data: { session: latest } } = await supabase.auth.getSession();
      await refreshSessionData(latest || null);
    } catch (e) {
      await refreshSessionData(null);
    }
  };

  return { user, session, loadingSession, initialAuthCheckComplete, userRole, isEmailConfirmed, lastAuthEvent, refreshAuthData };
}

export { useAuthSession };
  
